import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Table from '../../../core/common/dataTable/index';
import { RootState } from '../../../core/data/redux/store';
import {
    setVillages,
    setLoading,
    setError,
    deleteVillages,
} from '../../../core/data/redux/stateDistrictSlice';
import {
    fetchVillages,
    deleteVillage as deleteVillageService,
} from '../../../core/data/services/stateDistrictBlockService';
import { Village } from '../../../core/data/types/stateDistrict';
import VillageForm from './villageForm';
import { getUserDetails } from '../../../lib/getUserDetails';

const BlockAdmin = () => {
    const dispatch = useDispatch();
    const { villages, loading } = useSelector((state: RootState) => state.stateDistrict);

    const blockAdminDetails = getUserDetails();
    const stateId =blockAdminDetails[0]?.relatedEntity?.blockAreas?.stateId;
    const districtId =blockAdminDetails[0]?.relatedEntity?.blockAreas?.districtId;
    const blockId =blockAdminDetails[0]?.relatedEntity?.blockAreas?.id;


 
    const userStateId = stateId; 
    const userDistrictId = districtId; 
    const userBlockId =parseInt(blockId) ; 

    // State management
    const [selectedState] = useState<number>(userStateId); // Locked to user's state
    const [selectedDistrict] = useState<number>(userDistrictId); // Locked to user's district
    const [selectedBlock] = useState<number>(userBlockId); // Locked to user's block
    const [selectedVillage, setSelectedVillage] = useState<number | undefined>(undefined);
    const [showVillageForm, setShowVillageForm] = useState(false);
    const [editVillage, setEditVillage] = useState<Village | undefined>(undefined);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteItem, setDeleteItem] = useState<{ id: number; type: 'village' } | null>(null);

    // Fetch villages for the user's block on mount
    useEffect(() => {
        const loadVillages = async () => {
            dispatch(setLoading(true));
            try {
                const response = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
                dispatch(setVillages(response));
            } catch (err: any) {
                dispatch(setError(err.message || 'Failed to fetch villages'));
                dispatch(setVillages([]));
            } finally {
                dispatch(setLoading(false));
            }
        };
        loadVillages();
    }, [selectedState, selectedDistrict, selectedBlock, dispatch]);

    // Table columns with action buttons
    const villageColumns = [
        { title: 'ID', dataIndex: 'id', sorter: (a: Village, b: Village) => a.id - b.id },
        { title: 'Name', dataIndex: 'name', sorter: (a: Village, b: Village) => a.name.localeCompare(b.name) },
        { title: 'Block ID', dataIndex: 'blockAreaId', sorter: (a: Village, b: Village) => a.blockAreaId - b.blockAreaId },
        {
            title: 'Actions',
            render: (record: Village) => (
                <>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                            setEditVillage(record);
                            setShowVillageForm(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                            setDeleteItem({ id: record.id, type: 'village' });
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>
                </>
            ),
        },
    ];

    // Filter villages based on selection
    const filteredVillages = Array.isArray(villages)
        ? selectedVillage
            ? villages.filter((village) => village.id === selectedVillage && village.blockAreaId === selectedBlock)
            : villages.filter((village) => village.blockAreaId === selectedBlock)
        : [];

    const handleDelete = async () => {
        if (!deleteItem) return;
        try {
            dispatch(setLoading(true));
            if (deleteItem.type === 'village') {
                await deleteVillageService(deleteItem.id);
                dispatch(deleteVillages(deleteItem.id));
                setSelectedVillage(undefined);
                const villages = await fetchVillages(selectedState, selectedDistrict, selectedBlock);
                dispatch(setVillages(villages));
            }


            setShowDeleteModal(false);
            setDeleteItem(null);
        } catch (err: any) {
            dispatch(setError(err.message || 'Failed to delete'));
        } finally {
            dispatch(setLoading(false));
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="my-auto mb-2">
                        <h3 className="page-title">Block Admin Management</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    Block Name
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {blockAdminDetails[0]?.relatedEntity?.blockAreas?.name}
                                </li>
                            </ol>
                        </nav>
                    </div>
                 
                    <Button
                        variant="primary"
                        onClick={() => {
                            setEditVillage(undefined);
                            setShowVillageForm(true);
                        }}
                    >
                        Add Village
                    </Button>
                </div>

                {/* Table */}
                <div className="card">
                    <div className="card-body p-0 py-3">
                        {loading ? (
                            <div className="text-center py-5">
                                <i className="bi bi-arrow-repeat animate-spin" style={{ fontSize: '3rem' }}></i>
                                <h5 className="mt-3">Loading...</h5>
                            </div>
                        ) : filteredVillages.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="mt-3 text-muted">No Villages Found</h5>
                            </div>
                        ) : (
                            <Table
                                dataSource={filteredVillages}
                                columns={villageColumns}
                                Selection={false}
                                pagination={true}
                            />
                        )}
                    </div>
                </div>

                {/* Modals */}
                <VillageForm
                    show={showVillageForm}
                    onHide={() => setShowVillageForm(false)}
                    village={editVillage}
                    stateId={selectedState}
                    districtId={selectedDistrict}
                    blockAreaId={selectedBlock}
                />

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this village?
                    </Modal.Body>
                    <Modal.Footer className='gap-2'>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDelete}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default BlockAdmin;